import { memo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

/**
 * HomepageTopicalAuthority – Deep informational SEO hub
 * 
 * PURPOSE: Build topical authority for THCA & Magic Sauce,
 * increase dwell time, reduce bounce rate, strengthen E-E-A-T.
 * 
 * STRUCTURE:
 * H2: Vad är THCA?
 * H2: Vad är Magic Sauce vape?
 * H2: Vad menas med laglig cannabis i Sverige?
 * H2: Fördelar med THCA (vad säger forskningen?)
 * 
 * TOTAL: ~1,000-1,200 words
 * TONE: Neutral, educational, authority-driven
 * NO: Medical advice, sales copy, emoji, keyword stuffing
 */

export const HomepageTopicalAuthority = memo(() => {
  return (
    <section className="py-16 md:py-20 bg-card/20 border-t border-border/30">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">

          {/* Two-column grid on desktop, single column on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">

            {/* COLUMN 1 */}
            <div className="space-y-10">

              {/* H2: Vad är THCA? */}
              <article>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight">
                  Vad är THCA?
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    THCA, eller tetrahydrokannabinolsyra, är en naturligt förekommande 
                    cannabinoid som finns i råa cannabisväxter och industrihampa. Det är 
                    den kemiska föregångaren till THC (tetrahydrocannabinol), men de två 
                    ämnena har fundamentalt olika egenskaper.
                  </p>

                  <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
                    Skillnaden mellan THCA och THC
                  </h3>
                  <p>
                    Den avgörande skillnaden ligger i molekylstrukturen. THCA innehåller 
                    en extra karboxylgrupp som gör att substansen inte binder till CB1-receptorerna 
                    i hjärnan på samma sätt som THC. Det innebär att THCA i sin naturliga, 
                    obearbetade form inte producerar psykoaktiva effekter. Först när THCA 
                    utsätts för värme – en process som kallas dekarboxylering – omvandlas 
                    det till THC.
                  </p>

                  <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
                    Vad är dekarboxylering?
                  </h3>
                  <p>
                    Dekarboxylering är den kemiska reaktion där THCA förlorar sin 
                    karboxylgrupp genom upphettning och omvandlas till THC. I en{" "}
                    <Link to="/kollektioner/thca-vapes" className="text-primary hover:underline">
                      THCA vape i Sverige
                    </Link>{" "}
                    sker denna process omedelbart vid inhalering, när vätskan värms 
                    upp av värmespiralen. Temperaturen och upphettningshastigheten 
                    påverkar hur effektiv omvandlingen blir.
                  </p>
                  <p>
                    I råa hampaväxter och obearbetade produkter som buds förblir THCA 
                    intakt så länge materialet inte upphettas. Detta är anledningen till 
                    att råa hampaprodukter och{" "}
                    <Link to="/kollektioner/thca-buds" className="text-primary hover:underline">
                      THCA buds
                    </Link>{" "}
                    inte producerar psykoaktiva effekter vid förtäring utan upphettning.
                  </p>
                  <p className="text-sm">
                    Fördjupning:{" "}
                    <Link to="/blogg/vad-ar-thca" className="text-primary hover:underline">
                      Komplett guide om vad THCA är
                    </Link>{" "}
                    och{" "}
                    <Link to="/blogg/hur-upphettning-paverkar-thca" className="text-primary hover:underline">
                      hur dekarboxylering fungerar
                    </Link>.
                  </p>
                </div>
              </article>

              {/* H2: Vad är Magic Sauce vape? */}
              <article>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight">
                  Vad är Magic Sauce vape?
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Magic Sauce är ett produktvarumärke inom THCA-segmentet som har 
                    etablerats som en av de mest efterfrågade serierna på den svenska 
                    marknaden. Det som särskiljer Magic Sauce vape från generiska 
                    THCA-produkter är en kombination av hög renhet, noggrant utvalda 
                    terpenprofiler och tredjepartstestning.
                  </p>

                  <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
                    Vad utmärker Magic Sauce?
                  </h3>
                  <p>
                    Varje Magic Sauce-enhet innehåller THCA-destillat med upp till 99 % 
                    renhet, 2 ml kapacitet och ger cirka 1 000 puffar. Terpenerna är 
                    botaniskt deriverade och återspeglar smakprofiler från etablerade 
                    cannabissorter. Samtliga produkter genomgår laboratorieanalys för 
                    potens, tungmetaller och lösningsmedelsrester.
                  </p>
                  <p>
                    Till skillnad från många generiska engångsvapes på marknaden 
                    publicerar{" "}
                    <Link to="/kollektioner/thca-vapes" className="text-primary hover:underline">
                      Magic Sauce vape i Sverige
                    </Link>{" "}
                    analysresultat direkt på produktsidorna. För användare som föredrar 
                    en mildare upplevelse finns Snozy-serien med cirka 80 % renhet som 
                    ett alternativ.
                  </p>
                  <p className="text-sm">
                    Se hela sortimentet:{" "}
                    <Link to="/kollektioner/thca-vapes" className="text-primary hover:underline">
                      THCA Vapes
                    </Link>.
                  </p>
                </div>
              </article>
            </div>

            {/* COLUMN 2 */}
            <div className="space-y-10">

              {/* H2: Vad menas med laglig cannabis i Sverige? */}
              <article>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight">
                  Vad menas med laglig cannabis i Sverige?
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Begreppet "laglig cannabis" refererar i Sverige till produkter 
                    som härrör från industrihampa och som inte innehåller 
                    narkotikaklassade substanser. Den juridiska gränsdragningen 
                    baseras på vilka specifika cannabinoider som finns med i 
                    Läkemedelsverkets narkotikaförteckning.
                  </p>

                  <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
                    Juridisk skillnad mellan THC och THCA
                  </h3>
                  <p>
                    THC (delta-9-tetrahydrocannabinol) är narkotikaklassat i Sverige 
                    enligt narkotikastrafflagen (1968:64). THCA däremot finns per 2026 
                    inte med på Läkemedelsverkets förteckning över kontrollerade 
                    substanser. Denna distinktion gör att produkter som innehåller 
                    THCA i sin naturliga form kan hanteras lagligt under nuvarande 
                    regelverk.
                  </p>
                  <p>
                    Inom EU tillåts odling av industrihampa med en THC-halt under 
                    0,2 % enligt{" "}
                    <a
                      href="https://agriculture.ec.europa.eu/farming/crop-productions-and-plant-based-products/hemp_en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      EU:s jordbrukspolitik
                      <ExternalLink className="w-3 h-3" />
                    </a>. 
                    Produkter framställda från certifierad hampa kan innehålla THCA 
                    utan att strida mot gällande EU-lagstiftning.
                  </p>
                  <p>
                    Det är viktigt att notera att lagstiftning kring cannabinoider 
                    är ett område under ständig utveckling. Nya substanser som THCB, 
                    THCH och HHC har nyligen narkotikaklassats i Sverige. Konsumenter 
                    bör regelbundet kontrollera uppdateringar via{" "}
                    <a
                      href="https://www.lakemedelsverket.se/sv/narkotika"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Läkemedelsverket
                      <ExternalLink className="w-3 h-3" />
                    </a>.
                  </p>
                  <p className="text-sm italic">
                    Observera: Informationen ovan utgör inte juridisk rådgivning. 
                    Kontakta en kvalificerad jurist för frågor om specifika situationer.
                  </p>
                  <p className="text-sm">
                    Relaterat:{" "}
                    <Link to="/thca-sverige" className="text-primary hover:underline">
                      THCA Sverige
                    </Link>{" "}
                    |{" "}
                    <Link to="/faq" className="text-primary hover:underline">
                      Vanliga frågor
                    </Link>
                  </p>
                </div>
              </article>

              {/* H2: Fördelar med THCA */}
              <article>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 leading-tight">
                  Fördelar med THCA – vad säger forskningen?
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Forskning kring THCA befinner sig i ett tidigt skede, men 
                    prekliniska studier har identifierat flera områden av intresse. 
                    Det är viktigt att understryka att resultaten huvudsakligen 
                    kommer från laboratorieförsök och djurstudier – inte från 
                    kliniska prövningar på människor.
                  </p>

                  <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
                    Prekliniska forskningsområden
                  </h3>
                  <p>
                    <strong className="text-foreground">Anti-inflammatorisk potential:</strong>{" "}
                    Studier publicerade i{" "}
                    <a
                      href="https://pubmed.ncbi.nlm.nih.gov/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      PubMed
                      <ExternalLink className="w-3 h-3" />
                    </a>{" "}
                    indikerar att THCA kan ha anti-inflammatoriska egenskaper genom 
                    interaktion med COX-2-enzymer. Dessa resultat är preliminära och 
                    har inte bekräftats i humanstudier.
                  </p>
                  <p>
                    <strong className="text-foreground">Neuroprotektiv forskning:</strong>{" "}
                    Forskning undersöker om THCA kan ha en skyddande effekt på 
                    nervceller. Prekliniska modeller har visat lovande resultat, 
                    men mekanismerna är inte fullständigt kartlagda.
                  </p>
                  <p>
                    <strong className="text-foreground">Aptitreglering:</strong>{" "}
                    Preliminära studier tyder på att THCA kan påverka aptitreglerande 
                    signalvägar, men forskningen är i ett tidigt stadium och det 
                    krävs mer data för att dra slutsatser.
                  </p>
                  <p>
                    Sammanfattningsvis finns det intressanta forskningsriktningar, 
                    men det är för tidigt att dra medicinska slutsatser om THCA:s 
                    effekter. Mer forskning, särskilt kliniska prövningar, behövs 
                    innan några hälsorelaterade påståenden kan stödjas.
                  </p>
                  <p className="text-sm italic border-l-2 border-primary/30 pl-4">
                    Detta är inte medicinsk rådgivning. Konsultera sjukvården för 
                    hälsorelaterade frågor. Informationen baseras på publicerad 
                    preklinisk forskning och är inte avsedd att diagnostisera, 
                    behandla eller förebygga sjukdomar.
                  </p>
                </div>
              </article>
            </div>
          </div>

          {/* Trust footer */}
          <div className="mt-12 pt-6 border-t border-border/30 text-center">
            <p className="text-sm text-muted-foreground">
              Senast uppdaterad: Februari 2026. Innehållet granskas regelbundet 
              och baseras på offentligt tillgängliga källor.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
});

HomepageTopicalAuthority.displayName = "HomepageTopicalAuthority";
export default HomepageTopicalAuthority;
